import { Dispatch, SetStateAction, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { DollarSign, Calculator, AlertTriangle, CheckCircle2 } from "lucide-react"

// ** Theme Components
import { Badge } from "@components/common/badge"
import { Label } from "@components/common/label"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { Textarea } from "@components/common/textarea"
import { Checkbox } from "@components/common/checkbox"
import { Alert, AlertDescription, AlertTitle } from "@components/common/alert"
import { Card, CardContent, CardHeader, CardTitle } from "@components/common/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"

// ** Types
import { DictType } from "@pma/types"

// ** Api Routes
import { BILL_APPROVE_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"

// ** Utils
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import BoqTablePreview from "./boq-table-preview"

interface BulkAdjustmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedBills: Array<{ id: string; name: string; amount: string; project: string }>
  setSelectedItems: Dispatch<SetStateAction<Array<any>>>
  onAdjust: CallableFunction
  bill: DictType
  finishApproval?: boolean
}

export function BulkAdjustmentDialog({
  open,
  onOpenChange,
  selectedBills,
  onAdjust,
  bill,
  finishApproval,
  setSelectedItems,
}: BulkAdjustmentDialogProps) {
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const newParams = new URLSearchParams(searchParam)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [reason, setReason] = useState("")
  const [retention, setRetention] = useState(bill?.retention_percent?.toString() || "")
  const [useDifferentNotes, setUseDifferentNotes] = useState(false)
  const [adjustments, setAdjustments] = useState<Record<string, string>>({})
  const [individualNotes, setIndividualNotes] = useState<Record<string, string>>({})

  const allBillItems = bill?._bill_items
  const adjustmentBillItems =
    allBillItems?.filter((item: DictType) =>
      selectedBills.some((bill) => bill == item.boq_item_id),
    ) || []

  const handleAmountChange = (billId: string, newAmount: string) => {
    setAdjustments((prev) => ({ ...prev, [billId]: newAmount }))
  }

  const handleAdjust = () => {
    let payload

    if (finishApproval) {
      payload = adjustmentBillItems.map((bill: DictType) => ({
        bill_item_id: bill?.id,
        amount: adjustments[bill.id] ? adjustments[bill.id] : bill.approved_amount || bill?.amount,
        declined: bill?.declined || false,
        message_text: useDifferentNotes ? individualNotes[bill.id] || "" : reason,
      }))
    } else {
      payload = adjustmentBillItems.map((bill: DictType) => ({
        bill_item_id: bill?.id,
        amount: adjustments[bill.id] || bill.amount,
        declined: bill?.declined || false,
        message_text: useDifferentNotes ? individualNotes[bill.id] || "" : reason,
      }))
    }

    handleBillApprovalMutation.mutate(payload)
  }

  const approveBill = async (billItems: DictType[]) => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILL_APPROVE_ENDPOINT,
      {
        bill_id: bill?.id,
        items: billItems,
        finish_approval: finishApproval,
        ...(finishApproval && retention ? { retention: parseFloat(retention) } : {}),
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const handleBillApprovalMutation = useMutation({
    mutationKey: ["approve-bill-items"],
    mutationFn: approveBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill items approved successfully") + " — " + translate("moved to financial manager signing"),
        variant: "success",
      })
      setSelectedItems([])
      onAdjust()
      newParams.set("revalidate", "bills")
      newParams.set("bill_tab", "signed")

      navigate(`?${newParams.toString()}`)
      resetForm()
      onOpenChange(false)
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "approve bill", translate),
        variant: "destructive",
      })
    },
  })

  const resetForm = () => {
    setReason("")
    setAdjustments({})
    setRetention("")
  }

  const handleCancel = () => {
    resetForm()
    onOpenChange(false)
  }

  const calculateTotals = () => {
    const originalTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      return (
        sum + Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      )
    }, 0)

    const newTotal = adjustmentBillItems?.reduce((sum: any, bill: any) => {
      const newAmount = adjustments[bill.id]
        ? Number.parseFloat(adjustments[bill.id]) &&
          Number.parseFloat(adjustments[bill.id]) *
            Number.parseFloat(bill?._boq_item?.discount_price)
        : Number.parseFloat(bill?.amount) * Number.parseFloat(bill?._boq_item?.discount_price)
      return sum + (isNaN(newAmount) ? 0 : newAmount)
    }, 0)

    const difference = newTotal - originalTotal
    const percentageChange = originalTotal > 0 ? (difference / originalTotal) * 100 : 0

    return { originalTotal, newTotal, difference, percentageChange }
  }

  const { originalTotal, newTotal, difference, percentageChange } = calculateTotals()

  const hasChanges = Object.keys(adjustments).some((billId) => {
    const originalAmount = Number.parseFloat(
      adjustmentBillItems?.find((b: DictType) => b.id == billId)?.amount || "0",
    )

    const newAmount = Number.parseFloat(adjustments[billId] || "0")
    return !isNaN(newAmount) && newAmount !== originalAmount
  })

  const handleIndividualNotesChange = (billId: string, newNotes: string) => {
    setIndividualNotes((prev) => ({
      ...prev,
      [billId]: newNotes,
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full flex-col overflow-y-auto md:max-w-2xl lg:max-w-3xl xl:max-w-7xl",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {finishApproval ? translate("Finalize Approval") : translate("Amount Approval")}
          </DialogTitle>
          <DialogDescription>
            {translate("Approve amounts for selected items")}. ({selectedBills.length})
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-6 py-4'>
          {!finishApproval && (
            <Alert>
              <AlertTriangle className='h-4 w-4' />
              <AlertTitle>{translate("Bulk Approval")}</AlertTitle>
              <AlertDescription>
                {translate("You are approving multiple bill items at once")}.{" "}
                {translate("Please review each amount carefully before submitting")}.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className='pb-3'>
              <CardTitle className='text-base'>{translate("Approval Summary")}</CardTitle>
            </CardHeader>

            <CardContent>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                {!finishApproval && (
                  <div>
                    <span className='text-muted-foreground'>{translate("Original Total")}:</span>
                    <p className='text-lg font-medium'>{formatCurrency(originalTotal, isHebrew)}</p>
                  </div>
                )}
                <div>
                  <span className='text-muted-foreground'>
                    {finishApproval ? translate("Bill total") : translate("Approved Total")}:
                  </span>
                  <p className='text-lg font-medium'>{formatCurrency(newTotal, isHebrew)}</p>
                </div>

                <div className='col-span-2 flex items-center justify-start gap-2 text-sm'>
                  <span className='text-muted-foreground'>{translate("Total Items")}:</span>
                  <Badge variant='secondary'>
                    {adjustmentBillItems?.length} {translate("items")}
                  </Badge>
                </div>
                {hasChanges && (
                  <div className='col-span-2'>
                    <div
                      className={`flex items-center gap-2 ${difference >= 0 ? "text-green-600" : "text-red-600"}`}
                    >
                      <Calculator className='h-4 w-4' />
                      <span>
                        {difference >= 0
                          ? translate("Total Increase")
                          : translate("Total Decrease")}
                        :{formatCurrency(Math.abs(difference), isHebrew)} (
                        {Math.abs(percentageChange).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className='grid gap-4'>
            <div className='flex items-center'>
              <Checkbox
                id='different-notes'
                checked={useDifferentNotes}
                onCheckedChange={(checked) => setUseDifferentNotes(checked === true)}
              />
              <Label
                htmlFor='different-notes'
                className={cn("text-sm font-normal", isHebrew ? "mr-2" : "ml-2")}
              >
                {translate("Use different notes for each bill item")}
              </Label>
            </div>

            {!useDifferentNotes ? (
              <div className='grid gap-2'>
                <Label htmlFor='adjustment-reason'>{translate("Reason")}</Label>
                <Textarea
                  id='adjustment-reason'
                  placeholder={translate("Provide comment") + "..."}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
                <p className='text-xs text-muted-foreground'>
                  {translate("This reason will be applied to all approved bills")}.
                </p>
              </div>
            ) : (
              <div className='space-y-4'>
                <Alert>
                  <CheckCircle2 className='h-4 w-4' />
                  <AlertTitle>{translate("Individual Notes")}</AlertTitle>
                  <AlertDescription>
                    {translate("You can provide different approval notes for each bill")}.
                  </AlertDescription>
                </Alert>

                <div className='max-h-96 space-y-4 overflow-y-auto'>
                  {adjustmentBillItems?.map((bill: DictType) => (
                    <Card key={bill?.id}>
                      <CardHeader className='pb-3'>
                        <CardTitle className='text-sm'>{bill?._boq_item?.number}</CardTitle>
                        <p className='text-xs text-muted-foreground'>
                          {translate("Requested")} :{bill.amount}
                        </p>
                        <div className='flex items-center gap-2'>
                          <h4 className='text-xs text-muted-foreground'>
                            {translate("Approved")} :
                          </h4>
                          <Input
                            type='number'
                            step='0.01'
                            inputMode='numeric'
                            disabled={finishApproval}
                            placeholder={bill?.approved_amount || bill?.amount}
                            value={adjustments[bill.id] || ""}
                            className='w-[140px] rounded-none border-x-0 border-b border-t-0 border-b-primary shadow-none'
                            onChange={(e) => handleAmountChange(bill.id, e.target.value)}
                          />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className='grid gap-2'>
                          <Label>{translate("Approval Notes")}</Label>
                          <Textarea
                            placeholder={translate("Provide Comment")}
                            value={individualNotes[bill.id] || ""}
                            onChange={(e) => handleIndividualNotesChange(bill.id, e.target.value)}
                            rows={3}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {finishApproval && (
            <div className='grid gap-2'>
              <Label htmlFor='retention'>{translate("Retention")} (%)</Label>
              <Input
                id='retention'
                type='number'
                min='0'
                max='100'
                step='0.01'
                inputMode='numeric'
                placeholder={translate("Enter retention percentage")}
                value={retention}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === "" || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                    setRetention(value)
                  }
                }}
                className='max-w-xs'
              />
              <p className='text-xs text-muted-foreground'>
                {translate("Enter retention percentage value")} (0-100%)
              </p>
            </div>
          )}

          <div className='grid gap-2'>
            <Label>{translate("Bill Amount Approval")}</Label>
            <p className='text-sm'>
              {translate(
                "You can adjust the amounts before approving by updating the approved amount section",
              )}
              .
            </p>
            <div className='overflow-x-scroll rounded-lg border'>
              <BoqTablePreview
                title='Approved Amount'
                data={adjustmentBillItems}
                handleAmountChange={handleAmountChange}
                adjustments={adjustments}
              />
            </div>
          </div>
        </div>
        <DialogFooter className={isHebrew ? "!space-x-reverse" : ""}>
          <Button variant='outline' onClick={handleCancel}>
            {translate("Cancel")}
          </Button>
          <Button
            onClick={handleAdjust}
            loading={handleBillApprovalMutation.isPending}
            disabled={handleBillApprovalMutation.isPending}
          >
            <DollarSign className='mr-1 h-4 w-4' />
            {finishApproval ? translate("Approve Bill") : translate("Submit Approval")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
